import api from './api'
import { browserLanguages, loadLocalePreference, resolveLocalePreference } from './localePreference'
import type { JobConnector, JobResume } from './jobs'

const text = (tr: string, en: string) => resolveLocalePreference(loadLocalePreference(), browserLanguages()) === 'tr' ? tr : en

/** Read only: the server owns execution and continuation of this exact job. */
export function createAiInstallationJobConnector(resume: JobResume): JobConnector {
  return handlers => {
    let stopped = false, polling = false
    let timer: number | undefined
    let lastStatus = ''
    const statusPath = resume.statusPath || ''
    const cleanup = () => {
      stopped = true
      if (timer) window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', wake)
      window.removeEventListener('focus', wake)
    }
    async function poll() {
      if (stopped || polling) return
      polling = true
      try {
        const { data } = await api.get(statusPath)
        if (stopped) return
        const operation = data?.operation
        if (!operation?.id || operation.id !== resume.operationId) {
          cleanup()
          handlers.appendLine(text('AI bileşen işinin kimliği doğrulanamadı; işlem yeniden başlatılmadı.\n', 'The AI component job identity could not be verified; the operation was not restarted.\n'))
          handlers.finish(false)
          return
        }
        const status = operation.status
        if (status !== lastStatus) {
          handlers.appendLine(status === 'deferred'
            ? text('⏸ Bekliyor: aktif AI çalışmaları bitince aynı işlem sunucuda otomatik sürdürülecek.\n', '⏸ Waiting: the same operation will resume on the server after active AI runs finish.\n')
            : status === 'completed' ? text('✓ Bileşen işlemi sunucuda tamamlandı.\n', '✓ The component operation completed on the server.\n')
            : `${text('İş durumu', 'Job status')}: ${status}\n`)
          lastStatus = status
        }
        if (status === 'completed') {
          cleanup()
          handlers.finish(true, { body: text('Bekleyen AI bileşen işlemi sunucuda tamamlandı.', 'The pending AI component operation completed on the server.'), url: '/ai-coding' })
          window.dispatchEvent(new CustomEvent('ai-installation-changed', { detail: { serverName: resume.serverName, operationId: resume.operationId } }))
        } else if (['failed', 'interrupted', 'unknown'].includes(status)) {
          cleanup()
          handlers.appendLine(String(operation.error || text('İşlem tamamlanamadı; sunucu durumunu kontrol edin.', 'The operation did not complete; check the server status.')))
          handlers.finish(false)
        } else if (status === 'deferred') {
          handlers.finish(false, { deferred: true, notify: false })
        } else if (!['queued', 'running'].includes(status)) {
          throw new Error('AI bileşen işi durumu doğrulanamadı')
        }
      } catch (error: any) {
        if (stopped) return
        const status = Number(error?.response?.status || 0)
        if (status && status < 500 && ![408, 429].includes(status)) {
          cleanup()
          handlers.appendLine(text('Kalıcı iş durumu okunamadı; işlem yeniden başlatılmadı.\n', 'The durable job status could not be read; the operation was not restarted.\n'))
          handlers.finish(false)
        }
        // A connection loss or background suspension never starts another job.
      } finally {
        polling = false
        if (!stopped) timer = window.setTimeout(poll, document.visibilityState === 'visible' ? 2500 : 15000)
      }
    }
    function wake() {
      if (document.visibilityState !== 'visible' || stopped || polling) return
      if (timer) window.clearTimeout(timer)
      void poll()
    }
    document.addEventListener('visibilitychange', wake)
    window.addEventListener('focus', wake)
    void poll()
    return { cleanup }
  }
}
